import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import CakeRoundedIcon from '@mui/icons-material/CakeRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import PageHeader from '../ui/PageHeader';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import { formatRelativeTime } from '../../utils/dateUtils';
import NotificationTypes from '../../constants/notificationTypes';
import '../../css/Notification.css';

const NotificationsPage = () => {
    const { notifications, loading, markAsSeen, markAllAsSeen } = useNotifications();
    const navigate = useNavigate();

    /** Click on a notification card — mark seen and navigate if actionable */
    const handleNotifClick = (notif) => {
        markAsSeen(notif.id);
        if (notif.notificationType === NotificationTypes.LEAVE_APPLIED) {
            navigate('/home/leave-approval');
        }
    };

    return (
        <Box sx={{ p: { xs: 1, sm: 2, md: 2 } }}>
            <PageHeader
                title="Notifications"
                right={
                    notifications.length > 0 ? (
                        <Tooltip title="Mark all as seen">
                            <IconButton
                                id="mark-all-seen-page-btn"
                                size="small"
                                onClick={markAllAsSeen}
                                className="notif-mark-all-btn"
                            >
                                <DoneAllRoundedIcon fontSize="small" color="primary" />
                            </IconButton>
                        </Tooltip>
                    ) : null
                }
            />

            {loading ? (
                <Box className="notif-page-loading">
                    <CircularProgress />
                </Box>
            ) : notifications.length === 0 ? (
                <Box className="notif-page-empty">
                    <Box className="notif-page-empty-icon-wrap">
                        <NotificationsNoneRoundedIcon className="notif-page-empty-icon" sx={{ color: 'text.disabled' }} />
                    </Box>
                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 800 }}>
                        All caught up!
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                        You have no new notifications.
                    </Typography>
                </Box>
            ) : (
                <Box className="notif-page-list">
                    {/* Summary */}
                    <Typography variant="caption" color="text.secondary" className="notif-page-summary">
                        {notifications.length} unread notification{notifications.length > 1 ? 's' : ''}
                    </Typography>

                    {notifications.map((notif) => (
                        <Card
                            key={notif.id}
                            id={`notif-card-${notif.id}`}
                            variant="outlined"
                            className={`notif-page-card notif-page-card--${notif.type}`}
                            onClick={() => handleNotifClick(notif)}
                            sx={{
                                cursor: notif.notificationType === NotificationTypes.LEAVE_APPLIED ? 'pointer' : 'default',
                                '&:hover': notif.notificationType === NotificationTypes.LEAVE_APPLIED
                                    ? { borderColor: 'var(--primary)', bgcolor: 'rgba(4,88,100,0.03)' }
                                    : {},
                            }}
                        >
                            <CardContent className="notif-page-card-content">
                                {/* Icon */}
                                <Box className={`notif-page-icon-circle notif-page-icon-circle--${notif.type}`}>
                                    {notif.notificationType === NotificationTypes.LEAVE_APPLIED
                                        ? <EventNoteRoundedIcon sx={{ color: '#045864', fontSize: 22 }} />
                                        : notif.type === 'celebration'
                                            ? <CakeRoundedIcon sx={{ color: '#e91e63', fontSize: 22 }} />
                                            : <WarningAmberRoundedIcon sx={{ color: '#ff9800', fontSize: 22 }} />
                                    }
                                </Box>

                                {/* Text */}
                                <Box className="notif-page-text-area">
                                    <Typography className="notif-page-message-text">
                                        {notif.message}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.75 }}>
                                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11, fontWeight: 600 }}>
                                            {formatRelativeTime(notif.createdAt)}
                                        </Typography>
                                        {notif.notificationType === NotificationTypes.LEAVE_APPLIED && (
                                            <Typography variant="caption" sx={{ color: 'var(--primary)', fontSize: 11, fontWeight: 700 }}>
                                                Click to review →
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>

                                {/* Dismiss */}
                                <Tooltip title="Mark as seen">
                                    <IconButton
                                        id={`dismiss-page-notif-${notif.id}`}
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); markAsSeen(notif.id); }}
                                        className="notif-page-dismiss-btn"
                                        sx={{ color: 'text.disabled' }}
                                    >
                                        <CloseRoundedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default NotificationsPage;
